/**
 * `/settings/integrations` — provider catalogue.
 *
 * GitHub + Google Search Console are **live** connectors driven by their
 * backend status endpoints: each shows "Connect" when the app is
 * configured on this deployment, "Coming soon" when it isn't yet, and
 * "Connected" once an account is bound. The remaining providers stay as
 * static "coming soon" placeholders until they ship a backend.
 */
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toastError } from '@/lib/errors';
import {
  type GoogleScProperty,
  type IntegrationProvider,
  useGithubStatus,
  useGoogleConnect,
  useGoogleDisconnect,
  useGoogleScStatus,
  useGoogleUpdateProperty,
  useIntegrations,
  useOrigins,
} from '@/lib/queries';
import { GithubLogo, GoogleLogo, PuzzlePiece } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import type { ComponentType, ReactElement, ReactNode } from 'react';

export const Route = createFileRoute('/_app/settings/integrations')({
  component: IntegrationsSettings,
});

function IntegrationsSettings(): ReactElement {
  const integrations = useIntegrations();
  // Static placeholders, minus GitHub (now a live row below).
  const placeholders = (integrations.data ?? []).filter((p) => p.id !== 'github');

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Integrations</h2>
        <p className="text-sm text-[var(--color-mute)]">
          First-party connectors for popular tools. Vote on priorities via support@clickweave.co.
        </p>
      </header>

      <ul className="space-y-2">
        <GithubRow />
        <GoogleScRow />
        {placeholders.map((p) => (
          <PlaceholderRow key={p.id} provider={p} />
        ))}
      </ul>
    </div>
  );
}

// ─── Row shell ────────────────────────────────────────────────────────────────

function RowShell({
  icon: Icon,
  name,
  description,
  connected,
  children,
  footer,
}: {
  icon: ComponentType<{ size?: number; weight?: 'regular'; 'aria-hidden'?: boolean }>;
  name: string;
  description: string;
  connected?: boolean;
  children: ReactElement;
  /** Optional management panel rendered below the row (e.g. GSC property mappings). */
  footer?: ReactNode;
}): ReactElement {
  return (
    <li className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div
            className={`flex size-8 shrink-0 items-center justify-center ${
              connected ? 'text-[var(--color-success)]' : 'text-[var(--color-ink-soft)]'
            }`}
          >
            <Icon size={22} aria-hidden={true} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-ink)]">{name}</h3>
            <p className="text-xs text-[var(--color-mute)]">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">{children}</div>
      </div>
      {footer}
    </li>
  );
}

function ConnectedBadge({ label }: { label: string }): ReactElement {
  return (
    <span className="rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
      {label}
    </span>
  );
}

function ComingSoon(): ReactElement {
  return (
    <span className="rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs font-medium text-[var(--color-ink-soft)]">
      Coming soon
    </span>
  );
}

// ─── GitHub (App install) ───────────────────────────────────────────────────

function GithubRow(): ReactElement {
  const status = useGithubStatus();
  const data = status.data;
  return (
    <RowShell
      icon={GithubLogo}
      name="GitHub"
      description="Audit preview deploys on PRs and mark production deploys on your perf timeline."
      connected={data?.connected}
    >
      {status.isLoading ? (
        <Skeleton className="h-8 w-20" />
      ) : data?.connected ? (
        <ConnectedBadge label={`Connected · ${data.installation?.github_account_login ?? 'org'}`} />
      ) : data?.available && data.connect_url ? (
        <Button onClick={() => window.location.assign(data.connect_url!)}>Connect</Button>
      ) : (
        <ComingSoon />
      )}
    </RowShell>
  );
}

// ─── Google Search Console (OAuth) ────────────────────────────────────────────

function GoogleScRow(): ReactElement {
  const status = useGoogleScStatus();
  const connect = useGoogleConnect();
  const disconnect = useGoogleDisconnect();
  const data = status.data;
  const needsReauth = data?.connected && data.status === 'needs_reauth';

  async function startConnect(): Promise<void> {
    try {
      const { auth_url } = await connect.mutateAsync();
      window.location.assign(auth_url);
    } catch {
      toastError('Could not start Google connection', 'Please try again in a moment.');
    }
  }

  return (
    <RowShell
      icon={GoogleLogo}
      name="Google Search Console"
      description="See how performance work moves search clicks, impressions, and position."
      connected={data?.connected && !needsReauth}
      footer={
        data?.connected ? (
          <GooglePropertiesPanel
            properties={data.properties}
            needsReauth={!!needsReauth}
            onReconnect={() => void startConnect()}
            reconnecting={connect.isPending}
          />
        ) : undefined
      }
    >
      {status.isLoading ? (
        <Skeleton className="h-8 w-20" />
      ) : needsReauth ? (
        <>
          <span className="rounded-full bg-[var(--color-warning)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-warning)]">
            Needs re-authorisation
          </span>
          <Button loading={connect.isPending} onClick={() => void startConnect()}>
            Reconnect
          </Button>
        </>
      ) : data?.connected ? (
        <>
          <ConnectedBadge label={`Connected · ${data.email ?? 'account'}`} />
          <Button
            variant="ghost"
            loading={disconnect.isPending}
            onClick={() => disconnect.mutate()}
          >
            Disconnect
          </Button>
        </>
      ) : data?.available ? (
        <Button loading={connect.isPending} onClick={() => void startConnect()}>
          Connect
        </Button>
      ) : (
        <ComingSoon />
      )}
    </RowShell>
  );
}

/**
 * Property → site mapping panel under the connected GSC row. Each
 * discovered property gets a site picker (with a one-click suggestion
 * when a domain matches) and a sync toggle. Sync only runs for
 * properties that are BOTH mapped and enabled.
 */
function GooglePropertiesPanel({
  properties,
  needsReauth,
  onReconnect,
  reconnecting,
}: {
  properties: GoogleScProperty[];
  needsReauth: boolean;
  onReconnect: () => void;
  reconnecting: boolean;
}): ReactElement {
  const sites = useOrigins({ limit: 200 });
  const updateProperty = useGoogleUpdateProperty();
  const siteRows = sites.data?.sites ?? [];

  if (needsReauth) {
    return (
      <div className="mt-3 rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-3 py-2 text-xs text-[var(--color-mute)]">
        Google revoked our access (this happens after a password change or manual revoke). Search
        data stops syncing until you{' '}
        <button
          type="button"
          className="font-medium text-[var(--color-ink)] underline"
          disabled={reconnecting}
          onClick={onReconnect}
        >
          reconnect
        </button>
        .
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <p className="mt-3 text-xs text-[var(--color-mute)]">
        No Search Console properties found on this Google account. Add your site in Search Console
        first, then reconnect.
      </p>
    );
  }

  return (
    <ul className="mt-3 divide-y divide-[var(--color-line-soft)] rounded-lg border border-[var(--color-line)]">
      {properties.map((p) => {
        const suggestion = p.site_uuid ? null : suggestSiteForProperty(p.property_url, siteRows);
        return (
          <li
            key={p.uuid}
            className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[var(--color-ink)]">
                {p.property_url}
              </p>
              <p className="text-xs text-[var(--color-mute)]">
                {p.site_uuid
                  ? p.last_synced_at
                    ? `Last synced ${new Date(p.last_synced_at).toLocaleString()}`
                    : 'Mapped — first sync lands within a day'
                  : 'Not linked to a site — search data isn’t syncing'}
                {suggestion && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="font-medium text-[var(--color-ink)] underline"
                      onClick={() =>
                        updateProperty.mutate({
                          propertyUuid: p.uuid,
                          site_uuid: suggestion.uuid,
                          enabled: true,
                        })
                      }
                    >
                      Link to {suggestion.label}
                    </button>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={p.site_uuid ?? 'none'}
                onValueChange={(v) =>
                  updateProperty.mutate({
                    propertyUuid: p.uuid,
                    site_uuid: v === 'none' ? null : (v as string),
                  })
                }
              >
                <SelectTrigger className="w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectPopup>
                  <SelectItem value="none">Not linked</SelectItem>
                  {siteRows.map((s) => (
                    <SelectItem key={s.uuid} value={s.uuid}>
                      {siteLabel(s)}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
              <span className="flex items-center gap-1.5 text-xs text-[var(--color-mute)]">
                <Switch
                  aria-label={`Sync ${p.property_url}`}
                  checked={p.enabled}
                  onCheckedChange={(checked) =>
                    updateProperty.mutate({ propertyUuid: p.uuid, enabled: checked })
                  }
                />
                Sync
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

interface SiteRowLike {
  uuid: string;
  origin?: string | null;
  name?: string | null;
}

function siteLabel(s: SiteRowLike): string {
  return s.name || hostOf(s.origin) || s.uuid;
}

function hostOf(origin: string | null | undefined): string | null {
  if (!origin) return null;
  try {
    return new URL(origin).host;
  } catch {
    return null;
  }
}

/**
 * Domain-match auto-suggest: `sc-domain:example.com` matches any site
 * whose origin host is example.com or a subdomain of it; a URL-prefix
 * property matches on exact host.
 */
function suggestSiteForProperty(
  propertyUrl: string,
  sites: SiteRowLike[],
): { uuid: string; label: string } | null {
  const domain = propertyUrl.startsWith('sc-domain:')
    ? propertyUrl.slice('sc-domain:'.length).toLowerCase()
    : hostOf(propertyUrl)?.toLowerCase();
  if (!domain) return null;
  const match = sites.find((s) => {
    const host = hostOf(s.origin)?.toLowerCase();
    return !!host && (host === domain || host.endsWith(`.${domain}`));
  });
  return match ? { uuid: match.uuid, label: siteLabel(match) } : null;
}

// ─── Static placeholder rows ──────────────────────────────────────────────────

function PlaceholderRow({ provider }: { provider: IntegrationProvider }): ReactElement {
  return (
    <RowShell icon={PuzzlePiece} name={provider.name} description={provider.description}>
      <ComingSoon />
    </RowShell>
  );
}
